import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { X, Tag, Megaphone, ArrowRight, Sparkles, Flame } from 'lucide-react';
import { PromoAPI, formatCurrency } from '../mock';
import { useAuth } from '../context/AuthContext';

// Where the popup should NOT appear (admin views, checkout flow, etc.)
const BLOCKED_PATH_PREFIXES = [
  '/admin', '/cart', '/checkout', '/order/', '/track-order',
  '/login', '/auth', '/contact',
];

const STORAGE_KEY = 'tai_announcement_v1';     // { hash, dismissed_at }
const SNOOZE_HOURS = 12;                        // re-show after this if same content
const SHOW_DELAY_MS = 1800;                     // small delay so it doesn't feel intrusive

// Build a small content hash so a NEW notice/sale re-pops the modal
function contentHash(bundle) {
  const ids = [
    ...((bundle.notices || []).map(n => `n:${n.id}:${n.updated_at || ''}`)),
    ...((bundle.sale_vehicles || []).map(v => `v:${v.id}:${v.sale_price || v.price || ''}`)),
    ...((bundle.sale_parts || []).map(p => `p:${p.id}:${p.sale_price || p.price || ''}`)),
  ];
  return ids.sort().join('|') || '';
}

function readDismiss() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'); } catch { return null; }
}

function writeDismiss(hash) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ hash, dismissed_at: Date.now() })); } catch { /* ignore */ }
}

// Small card used for both notice & sale items
function PromoCard({ image, eyebrow, title, sub, priceNow, priceWas, currency, onClick, accent = 'red' }) {
  const tint = accent === 'red' ? 'bg-[#e11d2c]' : 'bg-black';
  return (
    <button
      type="button"
      onClick={onClick}
      className="group text-left w-full bg-white border border-black/10 hover:border-[#e11d2c] rounded-2xl overflow-hidden flex transition-all"
      data-testid="promo-card"
    >
      <div className="w-24 sm:w-28 shrink-0 bg-[#fafafa] aspect-square overflow-hidden">
        {image ? (
          <img src={image} alt={title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className={`w-full h-full grid place-items-center text-white ${tint}`}>
            <Megaphone size={22} />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 p-3 sm:p-4 flex flex-col">
        <div className={`text-[9px] tracking-[0.22em] uppercase font-bold ${accent === 'red' ? 'text-[#e11d2c]' : 'text-black/60'}`}>{eyebrow}</div>
        <div className="font-semibold text-[14px] sm:text-[15px] leading-tight mt-0.5 line-clamp-2">{title}</div>
        {sub && <div className="text-[11px] text-black/55 mt-0.5 line-clamp-1">{sub}</div>}
        <div className="mt-auto pt-2 flex items-baseline gap-2">
          {priceNow != null && (
            <span className="font-display text-[16px] font-black text-[#e11d2c]">{formatCurrency(priceNow, currency)}</span>
          )}
          {priceWas != null && priceWas > (priceNow || 0) && (
            <span className="text-[12px] text-black/45 line-through">{formatCurrency(priceWas, currency)}</span>
          )}
          <span className="ml-auto text-[#e11d2c] inline-flex items-center text-[11px] font-bold opacity-0 group-hover:opacity-100 transition-opacity">
            View <ArrowRight size={11} className="ml-1" />
          </span>
        </div>
      </div>
    </button>
  );
}

export default function AnnouncementPopup() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [bundle, setBundle] = useState(null);

  const onBlockedRoute = BLOCKED_PATH_PREFIXES.some(p => location.pathname.startsWith(p));

  // Fetch promo bundle once on first mount of an allowed route
  useEffect(() => {
    if (onBlockedRoute) return;
    if (user?.role === 'admin') return;       // don't nag admins on their own site
    let alive = true;
    PromoAPI.active()
      .then((data) => { if (alive) setBundle(data); })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Decide whether to open after bundle arrives
  useEffect(() => {
    if (!bundle) return;
    if (onBlockedRoute) return;
    const hasContent = (bundle.notices?.length || 0) + (bundle.sale_vehicles?.length || 0) + (bundle.sale_parts?.length || 0) > 0;
    if (!hasContent) return;

    const hash = contentHash(bundle);
    const prev = readDismiss();
    const stillFresh = prev && prev.hash === hash && (Date.now() - (prev.dismissed_at || 0) < SNOOZE_HOURS * 3600 * 1000);
    if (stillFresh) return;

    const t = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [bundle, onBlockedRoute]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onEsc = (e) => { if (e.key === 'Escape') handleDismiss(); };
    window.addEventListener('keydown', onEsc);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onEsc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleDismiss = () => {
    setOpen(false);
    if (bundle) writeDismiss(contentHash(bundle));
  };

  const handleNavigate = (to) => {
    handleDismiss();
    navigate(to);
  };

  const notice = bundle?.notices?.[0];
  const extraNotices = (bundle?.notices || []).slice(1, 3);
  const saleVehicles = bundle?.sale_vehicles || [];
  const saleParts = bundle?.sale_parts || [];
  const hasSales = saleVehicles.length + saleParts.length > 0;

  const heroTitle = notice?.title || (hasSales ? 'Hot deals just landed' : '');
  const heroBody = notice?.body || (hasSales ? 'Limited-time savings on vehicles & parts. Tap any item to learn more.' : '');

  if (!open || !bundle) return null;

  return (
    <div
      className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Announcements"
      data-testid="announcement-popup"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close announcements"
        onClick={handleDismiss}
        className="absolute inset-0 bg-black/55 backdrop-blur-[2px] animate-in fade-in duration-300"
        data-testid="announcement-backdrop"
      />

      {/* Card */}
      <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45)] overflow-hidden animate-in slide-in-from-bottom-6 sm:zoom-in-95 fade-in duration-500 max-h-[88vh] flex flex-col">
        {/* Header */}
        <div className="relative bg-black text-white p-5 sm:p-7">
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Close"
            data-testid="announcement-close"
            className="absolute top-4 right-4 w-9 h-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <X size={16} />
          </button>
          <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase font-bold text-[#e11d2c]">
            {notice ? <><Megaphone size={12} /> Announcement</> : <><Flame size={12} /> Hot deals</>}
          </div>
          <h2 className="font-display text-[24px] sm:text-[30px] font-black tracking-tight leading-tight mt-2 pr-10">
            {heroTitle || "What's new at Timeless"}
          </h2>
          {heroBody && (
            <p className="text-[13px] sm:text-[14px] text-white/75 leading-relaxed mt-2 whitespace-pre-line line-clamp-3">
              {heroBody}
            </p>
          )}
          {notice && (
            <button
              type="button"
              onClick={() => handleNavigate(`/notices/${notice.id}`)}
              className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-bold text-white bg-[#e11d2c] hover:bg-[#c01526] rounded-full px-4 h-9 transition-colors"
              data-testid="announcement-read-more"
            >
              Read full notice <ArrowRight size={13} />
            </button>
          )}
        </div>

        {/* Scroll body */}
        <div className="overflow-y-auto p-5 sm:p-7 space-y-5">
          {/* Extra pinned notices summary */}
          {extraNotices.length > 0 && (
            <div className="space-y-2">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-black/55 inline-flex items-center gap-1.5">
                <Sparkles size={11} /> Also worth a look
              </div>
              <div className="divide-y divide-black/5 border border-black/10 rounded-2xl overflow-hidden">
                {extraNotices.map((n) => (
                  <button
                    type="button"
                    key={n.id}
                    onClick={() => handleNavigate(`/notices/${n.id}`)}
                    className="w-full flex items-center gap-3 p-3 hover:bg-[#fafafa] text-left transition-colors"
                  >
                    <span className="w-8 h-8 grid place-items-center rounded-full bg-black/5"><Megaphone size={14} className="text-[#e11d2c]" /></span>
                    <span className="flex-1 min-w-0">
                      <span className="block font-semibold text-[14px] leading-tight line-clamp-1">{n.title}</span>
                      {n.body && <span className="block text-[12px] text-black/55 line-clamp-1 mt-0.5">{n.body}</span>}
                    </span>
                    <ArrowRight size={14} className="text-black/35 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sale vehicles */}
          {saleVehicles.length > 0 && (
            <div className="space-y-2">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#e11d2c] inline-flex items-center gap-1.5">
                <Tag size={11} /> Vehicles on sale
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {saleVehicles.slice(0, 4).map((v) => (
                  <PromoCard
                    key={v.id}
                    image={(v.photos || [])[0]}
                    eyebrow="Save now"
                    title={`${v.year || ''} ${v.make || ''} ${v.model || ''}`.trim()}
                    sub={v.status === 'on_sale' ? 'Limited-time offer' : ''}
                    priceNow={v.sale_price || v.price}
                    priceWas={v.sale_price ? v.price : null}
                    currency="jmd"
                    onClick={() => handleNavigate(`/vehicle/${v.id}`)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Sale parts */}
          {saleParts.length > 0 && (
            <div className="space-y-2">
              <div className="text-[10px] tracking-[0.22em] uppercase font-bold text-[#e11d2c] inline-flex items-center gap-1.5">
                <Tag size={11} /> Parts on sale
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {saleParts.slice(0, 4).map((p) => (
                  <PromoCard
                    key={p.id}
                    image={(p.images || [])[0]}
                    eyebrow={p.category || 'Parts'}
                    title={p.name}
                    sub={p.sku ? `SKU ${p.sku}` : ''}
                    priceNow={p.sale_price || p.price}
                    priceWas={p.sale_price ? p.price : null}
                    currency={p.currency || 'jmd'}
                    onClick={() => handleNavigate(`/parts/${p.id}`)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-black/10 p-4 sm:p-5 bg-[#fafafa] flex flex-wrap items-center justify-between gap-3">
          <span className="text-[11px] text-black/55">
            We'll snooze this for {SNOOZE_HOURS}h after you close it.
          </span>
          <div className="flex items-center gap-2 ml-auto">
            {hasSales && (
              <button
                type="button"
                onClick={() => handleNavigate(saleVehicles.length > 0 ? '/inventory?onSale=1' : '/parts?onSale=1')}
                className="h-10 px-5 rounded-full bg-[#e11d2c] hover:bg-[#c01526] text-white text-[12px] font-bold tracking-wider uppercase transition-colors"
                data-testid="announcement-see-all"
              >
                See all deals
              </button>
            )}
            <button
              type="button"
              onClick={handleDismiss}
              className="h-10 px-5 rounded-full border border-black/15 hover:border-black text-[12px] font-bold tracking-wider uppercase transition-colors"
              data-testid="announcement-dismiss"
            >
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
